import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { addContributionToActive } from '@/components/agent/tools/_shared/applyContribution'
import { useDocumentOverlayOptional } from '@/components/document-overlay/DocumentDetailOverlayContext'
import type { FullEntityDetail } from '@/components/document-overlay/EntityInspector'
import {
  formatPropValue,
  mapEntityDetailToFullDetail,
} from '@/components/investigations/entity-detail/mapEntityDetail'
import { edgeFromRelationship, makeMeta, nodeFromPerson } from '@/features/graph/utils/builders'
import { caseKeys, useCaseGraphEntityDetail } from '@/hooks/use-case-queries'
import { api } from '@/services/api'
import type { DocumentEntity } from '@/services/api/cases'
import { graphApi } from '@/services/api/graph'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'
import { getEntityDisplayLabel } from '@/utils/entity-display'
import { hasGeoCoordinates } from '@/utils/geo'

// `source_filename` surfaces on the document chrome — hide it from the
// per-entity property list to avoid duplicating it inline.
const HIDDEN_PROPERTY_KEYS = new Set(['source_filename'])

interface UseEntityRowArgs {
  caseId: string
  documentId: string
  entity: DocumentEntity
}

/**
 * All IO + derived state for a single expandable entity row: the on-demand
 * entity detail query, edit / save / delete mutations, "add to graph" and
 * "open in graph" navigation actions, and the suspect-flag toggle.
 */
export function useEntityRow({ caseId, documentId, entity }: UseEntityRowArgs) {
  const navigate = useNavigate()
  const overlay = useDocumentOverlayOptional()
  const queryClient = useQueryClient()

  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editedProps, setEditedProps] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [togglingSuspect, setTogglingSuspect] = useState(false)
  const [openingGraph, setOpeningGraph] = useState(false)

  const detailQuery = useCaseGraphEntityDetail(caseId, entity.id, expanded)

  const entitySummary = useMemo(() => {
    const raw = entity.properties?._entity_summary
    if (raw == null) return null
    const text = formatPropValue(raw)
    return text.length > 0 ? text : null
  }, [entity.properties])

  const fullDetail: FullEntityDetail | null = useMemo(() => {
    if (!detailQuery.data) return null
    return mapEntityDetailToFullDetail(detailQuery.data)
  }, [detailQuery.data])

  const visibleProperties = useMemo(
    () => fullDetail?.properties.filter(p => !HIDDEN_PROPERTY_KEYS.has(p.key)) ?? [],
    [fullDetail]
  )

  const entityDisplayLabel = useMemo(
    () =>
      getEntityDisplayLabel({
        id: entity.id,
        name: entity.name ?? undefined,
        typeLabel: entity.label,
        properties: entity.properties,
      }),
    [entity]
  )

  const hasGeo = useMemo(() => hasGeoCoordinates(entity.properties), [entity.properties])

  const handleAddToGraph = useCallback(() => {
    const data = detailQuery.data
    if (!data) return
    const label = data.name?.trim() || entity.name?.trim() || entityDisplayLabel || entity.label
    const view: GraphView = {
      meta: {
        source: 'chips',
        createdAt: new Date().toISOString(),
        caseId,
        label,
      },
      nodes: [
        {
          id: data.id,
          labels: data.labels,
          typeLabel: entity.label,
          name: data.name ?? entity.name ?? undefined,
          properties: data.properties,
        },
      ],
      edges: [],
      view: emptyViewState(),
    }
    const store = getOrCreateInvestigationsStore({ caseId })
    addContributionToActive({ store, view, label })
    toast.success(`Added ${label} to graph`)
    overlay?.close()
    navigate(`/cases/${caseId}/graph`)
  }, [caseId, detailQuery.data, entity, entityDisplayLabel, navigate, overlay])

  const handleEnterEdit = useCallback(() => {
    const seed: Record<string, string> = {}
    for (const p of visibleProperties) seed[p.key] = p.value
    setEditedProps(seed)
    setEditing(true)
  }, [visibleProperties])

  const handleCancelEdit = useCallback(() => {
    setEditing(false)
    setEditedProps({})
  }, [])

  const handleChangeProp = useCallback((key: string, value: string) => {
    setEditedProps(prev => ({ ...prev, [key]: value }))
  }, [])

  const invalidateEntityCaches = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: caseKeys.graphEntityDetail(caseId, entity.id),
    })
    queryClient.invalidateQueries({ queryKey: caseKeys.documentDetail(caseId, documentId) })
    queryClient.invalidateQueries({ queryKey: ['case', caseId, 'knowledge-graph'] })
    queryClient.invalidateQueries({ queryKey: ['case', caseId, 'all-data'] })
  }, [caseId, documentId, entity.id, queryClient])

  const handleSave = useCallback(async () => {
    const changed: Record<string, string> = {}
    for (const original of visibleProperties) {
      const next = editedProps[original.key]
      if (next !== undefined && next !== original.value) {
        changed[original.key] = next
      }
    }
    if (Object.keys(changed).length === 0) {
      setEditing(false)
      setEditedProps({})
      return
    }
    setIsSaving(true)
    try {
      await api.graph.updateNodeProperties(caseId, entity.id, { properties: changed })
      invalidateEntityCaches()
      toast.success('Entity updated')
      setEditing(false)
      setEditedProps({})
    } catch (err: unknown) {
      console.error('Failed to update entity:', err)
      toast.error('Failed to update entity')
    } finally {
      setIsSaving(false)
    }
  }, [caseId, editedProps, entity.id, invalidateEntityCaches, visibleProperties])

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await api.graph.deleteNode(caseId, entity.id)
      invalidateEntityCaches()
      toast.success(`Deleted ${entityDisplayLabel}`)
      setConfirmDeleteOpen(false)
      setExpanded(false)
    } catch (err: unknown) {
      console.error('Failed to delete entity:', err)
      toast.error('Failed to delete entity')
    } finally {
      setIsDeleting(false)
    }
  }, [caseId, entity.id, entityDisplayLabel, invalidateEntityCaches])

  const handleMarkSuspect = useCallback(async () => {
    if (togglingSuspect || !fullDetail) return
    setTogglingSuspect(true)
    try {
      await graphApi.updateNodeProperties(caseId, entity.id, {
        properties: { is_suspect: !fullDetail.flaggedAsSuspect },
      })
      await queryClient.invalidateQueries({
        queryKey: caseKeys.graphEntityDetail(caseId, entity.id),
      })
    } catch (err: unknown) {
      console.error('Failed to toggle suspect flag:', err)
    } finally {
      setTogglingSuspect(false)
    }
  }, [caseId, entity.id, fullDetail, queryClient, togglingSuspect])

  const handleOpenInGraph = useCallback(async () => {
    if (openingGraph) return
    setOpeningGraph(true)
    try {
      const res = (await graphApi.getNodeNeighbors(caseId, entity.id, { hops: 2 })) as {
        nodes: Parameters<typeof nodeFromPerson>[0][]
        relationships: Parameters<typeof edgeFromRelationship>[0][]
      }
      const nodes = res.nodes.map(nodeFromPerson)
      const displayName =
        entity.name ??
        getEntityDisplayLabel({
          id: entity.id,
          name: entity.name ?? undefined,
          typeLabel: entity.label,
          properties: entity.properties,
        })
      if (!nodes.some(n => n.id === entity.id)) {
        nodes.unshift({
          id: entity.id,
          name: displayName,
          labels: [entity.label],
          typeLabel: entity.label,
          properties: entity.properties ?? {},
        })
      }
      const view = {
        meta: makeMeta(caseId, displayName),
        nodes,
        edges: res.relationships.map(edgeFromRelationship),
        view: emptyViewState(),
      }
      getOrCreateInvestigationsStore({ caseId }).getState().setTransient(view, displayName)
      navigate(`/cases/${caseId}/graph`)
    } catch (err: unknown) {
      console.error('Failed to open entity in graph:', err)
    } finally {
      setOpeningGraph(false)
    }
  }, [caseId, entity, navigate, openingGraph])

  return {
    detailQuery,
    expanded,
    setExpanded,
    editing,
    editedProps,
    isSaving,
    isDeleting,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    togglingSuspect,
    openingGraph,
    entitySummary,
    fullDetail,
    visibleProperties,
    entityDisplayLabel,
    hasGeo,
    handleAddToGraph,
    handleEnterEdit,
    handleCancelEdit,
    handleChangeProp,
    handleSave,
    handleConfirmDelete,
    handleMarkSuspect,
    handleOpenInGraph,
  }
}
